
import { useState } from "react";
import { useTranslations } from "use-intl";
import {
  Calendar,
  Clock,
  Hash,
  User,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import Select from "@/components/form/Select";
import { Button } from "@/components/ui/button";
import { useWorkflowRuns } from "@/hooks/api/queries/workflows.queries";
import RunStatusBadge from "@/features/workflows/components/RunStatusBadge";
import { RunDetailContent } from "../run-detail/run-detail-content";
import type { Workflow, WorkflowRun, RunStatus } from "@/types/workflow.types";

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString();
}

function formatDuration(ms?: number): string {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

interface RunsTabProps {
  workflow: Workflow;
}

export default function RunsTab({ workflow }: RunsTabProps) {
  const t = useTranslations("workflows");

  const STATUS_OPTIONS: { value: RunStatus | ""; label: string }[] = [
    { value: "", label: t("runs.filter_all_status") },
    { value: "pending", label: t("runs.filter_pending") },
    { value: "running", label: t("runs.filter_running") },
    { value: "success", label: t("runs.filter_success") },
    { value: "failed", label: t("runs.filter_failed") },
    { value: "cancelled", label: t("runs.filter_cancelled") },
  ];

  const [statusFilter, setStatusFilter] = useState<RunStatus | "">("");
  const [page, setPage] = useState(1);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const { data, isLoading } = useWorkflowRuns(workflow.id, {
    status: statusFilter || undefined,
    page,
    limit: 20,
  });
  const runs = data?.data || [];
  const paging = data?.paging;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select
          size="sm"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v as RunStatus | "");
            setPage(1);
          }}
          options={STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          className="w-36"
        />
      </div>

      {/* Runs list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      ) : runs.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-[var(--muted-foreground)]">
            {t("runs.empty_runs")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              workflowId={workflow.id}
              isExpanded={expandedRunId === run.id}
              onToggle={() =>
                setExpandedRunId(
                  expandedRunId === run.id ? null : run.id
                )
              }
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {paging && paging.total > 20 && (
        <div className="flex items-center justify-between border-t border-[var(--border)] pt-4">
          <p className="text-sm text-[var(--muted-foreground)]">
            {t("runs.showing_range", {
              start: (page - 1) * 20 + 1,
              end: Math.min(page * 20, paging.total),
              total: paging.total,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              {t("runs.btn_previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page * 20 >= paging.total}
            >
              {t("runs.btn_next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RunRow({
  run,
  workflowId,
  isExpanded,
  onToggle,
}: {
  run: WorkflowRun;
  workflowId: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations("workflows");

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border)]">
      {/* Run summary row */}
      <div className="flex items-center gap-2 px-4 py-3 transition-colors hover:bg-[var(--accent)]">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-4 text-left"
        >
          <RunStatusBadge status={run.status} />

          <div className="flex min-w-0 flex-1 items-center gap-4 text-xs text-[var(--muted-foreground)]">
            <span className="flex items-center gap-1">
              <Hash className="size-3" />
              #{run.id.slice(-6).toUpperCase()}
            </span>
            <span className="flex items-center gap-1">
              <User className="size-3" />
              {run.triggeredBy}
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <Calendar className="size-3" />
              {formatDate(run.startedAt)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="size-3" />
              {formatDuration(run.durationMs)}
            </span>
            {run.totalItemsProcessed > 0 && (
              <span className="hidden text-emerald-600 dark:text-emerald-400 sm:inline">
                {t("runs.items_processed", { count: run.totalItemsProcessed.toLocaleString() })}
              </span>
            )}
          </div>

          {isExpanded ? (
            <ChevronUp className="size-4 shrink-0 text-[var(--muted-foreground)]" />
          ) : (
            <ChevronDown className="size-4 shrink-0 text-[var(--muted-foreground)]" />
          )}
        </button>
        <Link
          to="/workflows/$id/runs/$runId"
          params={{ id: workflowId, runId: run.id }}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-[var(--muted-foreground)] transition-colors hover:bg-[var(--background)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          aria-label={t("run.open_full_view")}
          title={t("run.open_full_view")}
        >
          <ExternalLink className="size-4" aria-hidden />
        </Link>
      </div>

      {/* Expanded detail — shared RunDetailContent in inline mode */}
      {isExpanded && (
        <div className="border-t border-[var(--border)] bg-[var(--muted)]/30 px-4 py-4">
          <RunDetailContent
            workflowId={workflowId}
            runId={run.id}
            mode="inline"
          />
        </div>
      )}
    </div>
  );
}
